import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';

const DISMISS_KEY = 'longevity_pwa_install_dismissed_at';
const DISMISS_WINDOW_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

/**
 * Lightweight PWA install prompt.
 * - Listens for `beforeinstallprompt` (Chrome/Edge/Android).
 * - Shows an iOS Safari hint when install event isn't available but device looks iOS.
 * - Respects a 7-day dismiss window.
 */
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Already installed?
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return;
    // Recently dismissed?
    try {
      const prev = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (prev && Date.now() - prev < DISMISS_WINDOW_MS) return;
    } catch (lsErr) {
      console.debug('PWA dismiss-flag read failed:', lsErr);
    }

    const ua = navigator.userAgent || '';
    const iOSLike = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iOSLike);

    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBefore);

    // iOS Safari doesn't fire beforeinstallprompt — show hint after a small delay
    if (iOSLike) {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBefore);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', onBefore);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (lsErr) { console.debug('PWA dismiss-flag write failed:', lsErr); }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch (promptErr) { console.debug('PWA install prompt failed:', promptErr); }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:w-96 bg-white rounded-xl shadow-2xl border border-purple-200 p-4 z-50"
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
          <Download size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">Install Neon Alpha</p>
          <p className="text-sm text-gray-500">
            {isIOS && !deferredPrompt
              ? 'Tap the Share icon in Safari, then "Add to Home Screen".'
              : 'Add the app to your device for faster access and alerts on the go.'}
          </p>
          <div className="mt-3 flex gap-2">
            {deferredPrompt && (
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={install} data-testid="pwa-install-btn">
                Install
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={dismiss} data-testid="pwa-dismiss-btn">
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close"
          data-testid="pwa-close-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
